import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { AnonymizedTalent } from "../../models/AnonymizedTalent";
import { brandFontFamily, zoullooColors } from "../../theme";
import { sanitizeHtml } from "../../utils/sanitizeHtml";
import { ZOULLOO_PUBLIC_CONTACT } from "../../constants/zoullooContact";
import zoullooLogo from "../../assets/images/logo.png";

/**
 * V5.1 — "Premium bold disciplined" fiche, used:
 *  - On the public talent landing page (Option B), full-bleed
 *  - On any preview where we want the polished editorial look
 *
 * Translation strategy: every label coming from the API is a referential key
 * (e.g. "MachineData", "AerospaceDefense") that lives in the i18n bundle under
 * a known namespace (Skills.*, Tools.*, Sectors.*, BusinessDomains.*, ...).
 * The component pipes each label through `t()` with the existing convention
 * already used by `FreelancerDetails.tsx`. The fallback returns the raw label
 * so an unknown key still renders something legible.
 *
 * Presentation HTML: the freelancer's bio is authored in a Tiptap WYSIWYG and
 * therefore arrives with markup (`<p>`, `<strong>`, …). We render it through
 * `dangerouslySetInnerHTML` after running the input through a strict tag/attr
 * whitelist sanitizer (`utils/sanitizeHtml`). Untrusted markup is stripped.
 *
 * Style choices come from the validated charter PDF (Manrope, Coquelicot
 * #FF4300, Midnight Blue #281E69, Magnolia accent). No external image asset
 * required — all the visual interest comes from the layout + accents.
 */
interface Props {
	talent: AnonymizedTalent;
	/**
	 * Optional reference label shown in the top eyebrow.
	 * Defaults to a "ZL-<id>" reference reconstructed from the talent id.
	 */
	reference?: string;
	/**
	 * Optional language switcher rendered in the top eyebrow. Only the public
	 * page passes it; the in-app preview doesn't need it.
	 */
	languageSwitcher?: React.ReactNode;
}

// V5.1 charter accents — sourced from the theme so the public fiche stays
// aligned with any future brand tweak.
const MIDNIGHT = zoullooColors.brand.midnight;
const PERSIAN = zoullooColors.brand.persian;
const COQUELICOT = zoullooColors.brand.coquelicot;
const MAGNOLIA = zoullooColors.proposal.magnolia;

export const AnonymizedTalentFichePremium: React.FC<Props> = ({
	talent,
	reference,
	languageSwitcher
}) => {
	const { t } = useTranslation();
	const ref = reference ?? `ZL-${String(talent.id).padStart(6, "0")}`;

	return (
		<Box
			sx={{
				minHeight: "100vh",
				py: { xs: 4, md: 6 },
				px: { xs: 2, md: 4 },
				backgroundColor: MIDNIGHT,
				fontFamily: brandFontFamily,
				color: zoullooColors.grey[900]
			}}
		>
			<Box
				sx={{
					maxWidth: 880,
					margin: "0 auto",
					mb: 3,
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					color: "rgba(255,255,255,0.7)",
					fontSize: 13,
					flexWrap: "wrap",
					gap: 1
				}}
			>
				<span>{t("anonymizedTalent.topBar")}</span>
				<Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
					{languageSwitcher}
				</Box>
			</Box>

			<Box
				sx={{
					maxWidth: 880,
					margin: "0 auto",
					backgroundColor: zoullooColors.background.default,
					boxShadow: "0 32px 80px rgba(40, 30, 105, 0.5)",
					overflow: "hidden",
					borderRadius: 1
				}}
			>
				<Hero talent={talent} reference={ref} />
				<MainSections talent={talent} />
				<SloganBand />
				<FooterBar />
			</Box>
		</Box>
	);
};

// ============================================================
// Hero
// ============================================================
const Hero: React.FC<{ talent: AnonymizedTalent; reference: string }> = ({
	talent,
	reference
}) => {
	const { t } = useTranslation();
	const expertiseLabel = talent.expertise?.label
		? t(`Expertises.${talent.expertise.label}`, talent.expertise.label)
		: "";
	const experienceLabel = talent.experience?.label
		? t(`Experiences.${talent.experience.label}`, talent.experience.label)
		: "";
	// Translation namespace mirrors the rest of the app — note the plural form
	// ("FreelancingYearsOfExperiences", with the trailing s) used everywhere in
	// the freelancer onboarding / detail UI. Empty fallback (not "noValue") so
	// the corresponding stat card can be hidden when the data is missing.
	const yearsLabel = talent.freelancingYearsOfExperience?.label
		? t(
				`FreelancingYearsOfExperiences.${talent.freelancingYearsOfExperience.label}`,
				talent.freelancingYearsOfExperience.label
			)
		: "";

	const hasEvaluations = (talent.evaluations?.totalCount ?? 0) > 0;

	return (
		<Box
			sx={{
				background: `linear-gradient(135deg, ${MIDNIGHT} 0%, ${PERSIAN} 100%)`,
				color: "white",
				px: { xs: 4, md: 7 },
				py: { xs: 4, md: 7 },
				position: "relative",
				overflow: "hidden"
			}}
		>
			<Glow color={`rgba(255, 67, 0, 0.18)`} sx={{ top: -200, right: -200 }} />
			<Glow color="rgba(255, 255, 255, 0.04)" sx={{ bottom: -120, left: -120 }} />

			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					mb: 5,
					position: "relative",
					zIndex: 2
				}}
			>
				<AnonPill />
				<Typography sx={{ fontSize: 12, color: "rgba(255,255,255,0.6)" }}>
					{t("anonymizedTalent.reference")}{" "}
					<Box component="strong" sx={{ color: COQUELICOT, fontWeight: 700 }}>
						{reference}
					</Box>
				</Typography>
			</Box>

			<Box
				sx={{
					position: "relative",
					zIndex: 2,
					display: "grid",
					gridTemplateColumns: { xs: "1fr", md: "1fr 280px" },
					gap: 5,
					alignItems: "end"
				}}
			>
				<Box>
					<Typography
						sx={{
							fontSize: 12,
							fontWeight: 700,
							color: COQUELICOT,
							textTransform: "uppercase",
							letterSpacing: 2,
							mb: 1.5
						}}
					>
						{t("anonymizedTalent.eyebrow")}
					</Typography>
					<Typography
						sx={{
							fontSize: { xs: 80, md: 120 },
							fontWeight: 800,
							lineHeight: 0.9,
							letterSpacing: -5,
							mb: 2.5,
							color: "white"
						}}
					>
						{talent.firstNameInitial || "?"}
						<Box component="span" sx={{ color: COQUELICOT }}>
							.
						</Box>
						{talent.lastNameInitial || "?"}
						<Box component="span" sx={{ color: COQUELICOT }}>
							.
						</Box>
					</Typography>
					{/* Expertise first, as a tag — then the (often long) profile
					    title on its own line below. */}
					{expertiseLabel && (
						<Box
							component="span"
							sx={{
								display: "inline-block",
								mb: 1.75,
								px: 1.75,
								py: 0.625,
								borderRadius: 100,
								background: "rgba(255, 67, 0, 0.15)",
								border: "1px solid rgba(255, 67, 0, 0.4)",
								color: COQUELICOT,
								fontSize: 13,
								fontWeight: 700,
								letterSpacing: "0.3px"
							}}
						>
							{expertiseLabel}
						</Box>
					)}
					<Typography
						sx={{
							fontSize: 19,
							fontWeight: 500,
							lineHeight: 1.5,
							letterSpacing: "-0.3px",
							maxWidth: 480,
							color: "white"
						}}
					>
						{talent.title || t("anonymizedTalent.defaultTitle")}
					</Typography>
				</Box>

				{/* One stat card per line. Order: rating, then experience, then
				    freelance seniority. Each card is hidden when its data is
				    missing rather than showing a "—" placeholder. */}
				<Box sx={{ display: "grid", gridTemplateColumns: "1fr", gap: 1.25 }}>
					{hasEvaluations && (
						<StatCard featured label={t("anonymizedTalent.ratingLabel")}>
							<Typography
								sx={{
									fontSize: 22,
									fontWeight: 800,
									letterSpacing: "-0.5px",
									color: "white"
								}}
							>
								{Number(talent.evaluations?.averageRating ?? 0).toFixed(1)}{" "}
								<Box
									component="small"
									sx={{ fontSize: 12, fontWeight: 600, opacity: 0.7 }}
								>
									/ 5
								</Box>
							</Typography>
							<Box sx={{ display: "flex", gap: "1px", mt: 0.5 }}>
								{Array.from({ length: 5 }).map((_, idx) => (
									<Star
										key={idx}
										filled={idx < Math.round(Number(talent.evaluations?.averageRating ?? 0))}
									/>
								))}
							</Box>
						</StatCard>
					)}
					{experienceLabel && (
						<StatCard label={t("anonymizedTalent.experienceLabel")}>
							<Typography
								sx={{ fontSize: 22, fontWeight: 800, color: "white", lineHeight: 1 }}
							>
								{experienceLabel}
							</Typography>
						</StatCard>
					)}
					{yearsLabel && (
						<StatCard label={t("anonymizedTalent.freelanceLabel")}>
							<Typography
								sx={{ fontSize: 22, fontWeight: 800, color: "white", lineHeight: 1 }}
							>
								{yearsLabel}
							</Typography>
						</StatCard>
					)}
				</Box>
			</Box>
		</Box>
	);
};

// ============================================================
// Main content sections (01..05)
// ============================================================
const MainSections: React.FC<{ talent: AnonymizedTalent }> = ({ talent }) => {
	const { t } = useTranslation();
	const presentationHtml = useMemo(
		() => sanitizeHtml(talent.presentation),
		[talent.presentation]
	);

	const hasSectorsOrDomains =
		talent.sectors.length > 0 || talent.businessDomains.length > 0;

	// Build only the sections that actually have data, then number them
	// sequentially. Hiding an empty block must not leave a gap in the numbering
	// (01, 03, 05…), so the index — not a hard-coded number — drives the badge.
	const sections: { key: string; title: string; content: React.ReactNode }[] = [];

	if (presentationHtml) {
		sections.push({
			key: "about",
			title: t("anonymizedTalent.section1Title"),
			content: (
				<Box sx={{ pl: 3, borderLeft: `3px solid ${COQUELICOT}` }}>
					<Box
						sx={{
							fontSize: 15.5,
							lineHeight: 1.7,
							color: zoullooColors.grey[900],
							maxWidth: 660,
							"& p": { my: 0.75 },
							"& strong, & b": { fontWeight: 700, color: MIDNIGHT },
							"& ul, & ol": { pl: 2.5, my: 0.5 }
						}}
						dangerouslySetInnerHTML={{ __html: presentationHtml }}
					/>
					{talent.presentationTruncated && (
						<Typography
							sx={{
								display: "block",
								mt: 1.5,
								fontSize: 12,
								color: zoullooColors.grey[450],
								fontStyle: "italic",
								fontWeight: 500
							}}
						>
							{t("anonymizedTalent.presentationTruncated")}
						</Typography>
					)}
				</Box>
			)
		});
	}

	if (talent.topSkills.length > 0) {
		sections.push({
			key: "skills",
			title: t("anonymizedTalent.section2Title"),
			content: (
				<Box
					sx={{
						display: "grid",
						gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
						gap: 1.25
					}}
				>
					{talent.topSkills.map((s) => (
						<Box
							key={s.id}
							sx={{
								backgroundColor: "white",
								border: `1px solid ${zoullooColors.grey[200]}`,
								borderLeft: `3px solid ${COQUELICOT}`,
								px: 2.2,
								py: 1.5,
								borderRadius: 1,
								display: "flex",
								alignItems: "center",
								gap: 2
							}}
						>
							<Typography sx={{ fontSize: 14, fontWeight: 700, color: MIDNIGHT }}>
								{t(`Skills.${s.label}`, s.label)}
							</Typography>
						</Box>
					))}
				</Box>
			)
		});
	}

	if (talent.masteredTools.length > 0) {
		sections.push({
			key: "tools",
			title: t("anonymizedTalent.section3Title"),
			content: (
				<Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
					{talent.masteredTools.map((tool) => (
						<Box
							key={tool.id}
							sx={{
								px: 1.75,
								py: 1,
								backgroundColor: "white",
								border: `1px solid ${zoullooColors.grey[200]}`,
								color: MIDNIGHT,
								fontSize: 13,
								fontWeight: 600,
								borderRadius: 100,
								display: "inline-flex",
								alignItems: "center",
								gap: 0.875
							}}
						>
							<Box
								sx={{
									width: 5,
									height: 5,
									background: COQUELICOT,
									borderRadius: "50%"
								}}
							/>
							{t(`Tools.${tool.label}`, tool.label)}
						</Box>
					))}
				</Box>
			)
		});
	}

	if (hasSectorsOrDomains) {
		sections.push({
			key: "sectors",
			title: t("anonymizedTalent.section4Title"),
			content: (
				<Box
					sx={{
						backgroundColor: MAGNOLIA,
						p: 3.5,
						borderRadius: 1.5,
						display: "grid",
						gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
						gap: 4
					}}
				>
					<DomainCol
						label={t("anonymizedTalent.sectorsLabel")}
						values={talent.sectors.map((s) =>
							t(`Sectors.${s.sector.label}`, s.sector.label)
						)}
					/>
					<DomainCol
						label={t("anonymizedTalent.domainsLabel")}
						values={talent.businessDomains.map((d) =>
							t(
								`BusinessDomains.${d.businessDomain.label}`,
								d.businessDomain.label
							)
						)}
					/>
				</Box>
			)
		});
	}

	if (talent.professionalExperiences.length > 0) {
		sections.push({
			key: "missions",
			title: t("anonymizedTalent.section5Title"),
			content: (
				<Box
					sx={{
						display: "grid",
						gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
						gap: 1.5
					}}
				>
					{talent.professionalExperiences.map((xp) => (
						<MissionCard key={xp.id} xp={xp} />
					))}
				</Box>
			)
		});
	}

	if (sections.length === 0) {
		return null;
	}

	return (
		<Box sx={{ px: { xs: 4, md: 7 }, py: { xs: 4, md: 6 } }}>
			{sections.map((section, idx) => (
				<Section
					key={section.key}
					number={String(idx + 1).padStart(2, "0")}
					title={section.title}
				>
					{section.content}
				</Section>
			))}
		</Box>
	);
};

const SloganBand: React.FC = () => {
	const { t } = useTranslation();
	return (
		<Box
			sx={{
				background: MIDNIGHT,
				py: 4.5,
				px: { xs: 4, md: 7 },
				color: "white",
				position: "relative",
				overflow: "hidden",
				display: "flex",
				justifyContent: "space-between",
				alignItems: "center",
				flexDirection: { xs: "column", md: "row" },
				gap: 3
			}}
		>
			<Box
				sx={{
					position: "absolute",
					top: 0,
					left: 0,
					width: "100%",
					height: 3,
					background: COQUELICOT
				}}
			/>
			<Typography
				sx={{
					fontSize: 22,
					fontWeight: 700,
					letterSpacing: "-0.5px",
					lineHeight: 1.3,
					maxWidth: 480,
					position: "relative",
					zIndex: 1
				}}
			>
				{t("anonymizedTalent.slogan1")}
				<br />
				<Box
					component="em"
					sx={{ color: COQUELICOT, fontStyle: "italic", fontWeight: 800 }}
				>
					{t("anonymizedTalent.slogan2")}
				</Box>
			</Typography>

			{/* Commercial contact — prospect's entry point to talk to Zoulloo. */}
			<Box
				sx={{
					position: "relative",
					zIndex: 1,
					textAlign: { xs: "center", md: "right" }
				}}
			>
				<Typography
					sx={{
						fontSize: 10,
						textTransform: "uppercase",
						letterSpacing: 1.2,
						fontWeight: 700,
						color: "rgba(255,255,255,0.6)",
						mb: 0.75
					}}
				>
					{t("anonymizedTalent.contactLabel")}
				</Typography>
				<Typography sx={{ fontSize: 16, fontWeight: 800, color: "white" }}>
					{ZOULLOO_PUBLIC_CONTACT.name}
				</Typography>
				<Box
					component="a"
					href={`tel:${ZOULLOO_PUBLIC_CONTACT.phone.replace(/[^+\d]/g, "")}`}
					sx={{
						display: "block",
						fontSize: 13,
						color: "rgba(255,255,255,0.85)",
						textDecoration: "none",
						mt: 0.25,
						"&:hover": { color: COQUELICOT }
					}}
				>
					{ZOULLOO_PUBLIC_CONTACT.phone}
				</Box>
				<Box
					component="a"
					href={`mailto:${ZOULLOO_PUBLIC_CONTACT.email}`}
					sx={{
						display: "block",
						fontSize: 13,
						color: "rgba(255,255,255,0.85)",
						textDecoration: "none",
						mt: 0.25,
						"&:hover": { color: COQUELICOT }
					}}
				>
					{ZOULLOO_PUBLIC_CONTACT.email}
				</Box>
			</Box>
		</Box>
	);
};

const FooterBar: React.FC = () => {
	const { t } = useTranslation();
	return (
		<Box
			sx={{
				backgroundColor: zoullooColors.grey[100],
				color: zoullooColors.grey[450],
				py: 2.5,
				px: { xs: 4, md: 7 },
				display: "flex",
				justifyContent: "space-between",
				alignItems: "center",
				fontSize: 11,
				flexWrap: "wrap",
				gap: 1
			}}
		>
			<Box
				component="img"
				src={zoullooLogo}
				alt="zoulloo"
				sx={{ height: 22, width: "auto", display: "block" }}
			/>
			<span>{t("anonymizedTalent.diffusionRestricted")}</span>
			<a
				href="https://www.zoulloo.com"
				target="_blank"
				rel="noopener noreferrer"
				style={{
					color: zoullooColors.grey[450],
					textDecoration: "none"
				}}
			>
				www.zoulloo.com
			</a>
		</Box>
	);
};

// ============================================================
// Small UI primitives
// ============================================================
const Section: React.FC<{
	number: string;
	title: string;
	children: React.ReactNode;
}> = ({ number, title, children }) => (
	<Box sx={{ mb: 6 }}>
		<Box sx={{ display: "flex", alignItems: "center", gap: 1.75, mb: 3 }}>
			<Box
				sx={{
					width: 32,
					height: 32,
					border: `1.5px solid ${COQUELICOT}`,
					color: COQUELICOT,
					borderRadius: 1,
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					fontSize: 12,
					fontWeight: 800
				}}
			>
				{number}
			</Box>
			<Typography
				sx={{
					fontSize: 22,
					fontWeight: 800,
					color: MIDNIGHT,
					letterSpacing: "-0.5px"
				}}
			>
				{title}
			</Typography>
			<Box
				sx={{ flex: 1, height: 1, backgroundColor: zoullooColors.grey[200] }}
			/>
		</Box>
		{children}
	</Box>
);

const StatCard: React.FC<{
	label: string;
	featured?: boolean;
	children: React.ReactNode;
}> = ({ label, featured, children }) => (
	<Box
		sx={{
			background: featured
				? "rgba(255, 67, 0, 0.95)"
				: "rgba(255,255,255,0.08)",
			border: featured
				? `1px solid ${COQUELICOT}`
				: "1px solid rgba(255,255,255,0.12)",
			gridColumn: featured ? "1 / -1" : "auto",
			padding: 2,
			borderRadius: 1.25,
			backdropFilter: "blur(20px)"
		}}
	>
		<Typography
			sx={{
				fontSize: 10,
				textTransform: "uppercase",
				letterSpacing: 1,
				fontWeight: 700,
				color: featured ? "rgba(255,255,255,0.9)" : "rgba(255,255,255,0.6)",
				mb: 0.75
			}}
		>
			{label}
		</Typography>
		{children}
	</Box>
);

const Star: React.FC<{ filled: boolean }> = ({ filled }) => (
	<svg width={11} height={11} viewBox="0 0 24 24" aria-hidden>
		<path
			d="M12 2l3.09 6.26L22 9.27l-5 4.87L18.18 22 12 18.27 5.82 22 7 14.14 2 9.27l6.91-1.01z"
			fill={filled ? "white" : "rgba(255,255,255,0.25)"}
		/>
	</svg>
);

const Glow: React.FC<{ color: string; sx?: object }> = ({ color, sx }) => (
	<Box
		// Purely decorative — hide from assistive tech.
		aria-hidden="true"
		sx={{
			position: "absolute",
			width: 500,
			height: 500,
			background: `radial-gradient(circle, ${color} 0%, transparent 70%)`,
			borderRadius: "50%",
			pointerEvents: "none",
			...sx
		}}
	/>
);

const AnonPill: React.FC = () => {
	const { t } = useTranslation();
	return (
		<Box
			sx={{
				display: "inline-flex",
				alignItems: "center",
				gap: 1,
				background: "rgba(255, 67, 0, 0.15)",
				border: "1px solid rgba(255, 67, 0, 0.4)",
				color: COQUELICOT,
				px: 1.75,
				py: 0.875,
				borderRadius: 100,
				fontSize: 11,
				fontWeight: 700,
				letterSpacing: "0.5px",
				textTransform: "uppercase"
			}}
		>
			<Box
				sx={{
					width: 6,
					height: 6,
					background: COQUELICOT,
					borderRadius: "50%",
					boxShadow: `0 0 8px ${COQUELICOT}`
				}}
			/>
			{t("anonymizedTalent.anonymousPill")}
		</Box>
	);
};

const DomainCol: React.FC<{ label: string; values: string[] }> = ({
	label,
	values
}) => (
	<Box>
		<Typography
			sx={{
				fontSize: 10,
				letterSpacing: 1.2,
				textTransform: "uppercase",
				color: PERSIAN,
				fontWeight: 800,
				mb: 1.75
			}}
		>
			{label}
		</Typography>
		<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
			{values.length === 0 ? (
				<EmptyHint>—</EmptyHint>
			) : (
				values.map((v) => (
					<Box
						key={v}
						sx={{
							px: 1.5,
							py: 0.875,
							backgroundColor: "white",
							border: "1px solid rgba(40, 30, 105, 0.12)",
							color: MIDNIGHT,
							fontSize: 12.5,
							fontWeight: 700,
							borderRadius: 0.75
						}}
					>
						{v}
					</Box>
				))
			)}
		</Box>
	</Box>
);

const MissionCard: React.FC<{
	xp: AnonymizedTalent["professionalExperiences"][number];
}> = ({ xp }) => {
	const { t } = useTranslation();

	return (
		<Box
			sx={{
				backgroundColor: "white",
				border: `1px solid ${zoullooColors.grey[200]}`,
				borderRadius: 1.5,
				p: 2.75,
				position: "relative",
				overflow: "hidden"
			}}
		>
			<Box
				sx={{
					position: "absolute",
					top: 0,
					left: 0,
					bottom: 0,
					width: 3,
					background: COQUELICOT
				}}
			/>
			{xp.jobType?.label && (
				<Box
					sx={{
						position: "absolute",
						top: 0,
						right: 0,
						background: MIDNIGHT,
						color: "white",
						fontSize: 9.5,
						fontWeight: 800,
						letterSpacing: 0.6,
						px: 1.5,
						py: 0.625,
						borderBottomLeftRadius: 8,
						textTransform: "uppercase"
					}}
				>
					{t(`JobTypes.${xp.jobType.label}`, xp.jobType.label)}
				</Box>
			)}
			<Typography
				sx={{
					fontSize: 16,
					fontWeight: 800,
					color: MIDNIGHT,
					letterSpacing: "-0.3px",
					mt: 2,
					mb: 0.5
				}}
			>
				{xp.jobTitle ?? t("anonymizedTalent.missionDefaultRole")}
			</Typography>
			<Typography
				sx={{
					fontSize: 12.5,
					color: zoullooColors.grey[450],
					mb: 1.75,
					fontWeight: 500
				}}
			>
				{xp.companyName ?? t("anonymizedTalent.missionDefaultCompany")}
			</Typography>
			<TechStackText raw={xp.technicalStack} />
		</Box>
	);
};

/**
 * Renders the mission's technical stack as text, with its HTML interpreted.
 * The field is authored in Tiptap and arrives as HTML; per the product decision
 * we no longer split it into chips (which broke on grouped values such as
 * "Azure (Data Lake, Event Hub)"). We sanitize and render it as-is.
 */
const TechStackText: React.FC<{ raw: string | null | undefined }> = ({ raw }) => {
	const html = useMemo(() => sanitizeHtml(raw), [raw]);

	if (!html) {
		return null;
	}

	return (
		<Box
			sx={{
				fontSize: 12,
				lineHeight: 1.5,
				color: zoullooColors.grey[900],
				"& p": { my: 0.25 },
				"& strong, & b": { fontWeight: 700, color: MIDNIGHT },
				"& ul, & ol": { pl: 2, my: 0.25 }
			}}
			dangerouslySetInnerHTML={{ __html: html }}
		/>
	);
};

const EmptyHint: React.FC<{ children: React.ReactNode }> = ({ children }) => (
	<Typography
		sx={{
			fontSize: 13,
			color: zoullooColors.grey[450],
			fontStyle: "italic"
		}}
	>
		{children}
	</Typography>
);

export default AnonymizedTalentFichePremium;
